import React, { useEffect, useState } from "react";
import cartService from "../../services/cart.service";
import { profileService } from "../../services/profile.service";
import { couponService } from "../../services/coupon.service";
import "./CartPage.css";

const CartPage = ({
  onNavigate,
  cartItems,
  handleQuantityChange,
  removeItem,
  setCartItems,
}) => {
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loadingAddress, setLoadingAddress] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("ONLINE");
  const [userPhone, setUserPhone] = useState(null);

  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState("");

  // ===== TOAST STATE =====
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastTimer, setToastTimer] = useState(null);

  // ===== NEW: COUPON STATE =====
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponMessage, setCouponMessage] = useState({ type: "", text: "" });

  /* ---------------- Price & Tax Calculation ---------------- */
  const totals = cartItems.reduce(
    (acc, item) => {
      const mrp = Number(item.price || item.Inventory?.current_price || 0);
      const gstRate = Number(item.gst_rate || item.Inventory?.gst_rate || 18);
      let basePrice = Number(
        item.base_price || item.Inventory?.base_price || 0,
      );
      const qty = Number(item.quantity || 1);

      if (basePrice === 0) {
        basePrice = mrp / (1 + gstRate / 100);
      }

      const taxAmount = mrp - basePrice;

      acc.baseTotal += basePrice * qty;
      acc.taxTotal += taxAmount * qty;
      acc.grandTotal += mrp * qty;

      return acc;
    },
    { baseTotal: 0, taxTotal: 0, grandTotal: 0 },
  );

  const shipping = 300;

  // Apply discount to final total
  const discountAmount = appliedCoupon ? appliedCoupon.discountAmount : 0;
  const finalTotal = Math.max(0, totals.grandTotal - discountAmount) + shipping;

  const formatPrice = (price) =>
    `₹${price.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const uniqueGstRates = [
    ...new Set(
      cartItems.map((item) =>
        Number(item.gst_rate || item.Inventory?.gst_rate || 18),
      ),
    ),
  ];
  const gstLabel =
    uniqueGstRates.length === 1
      ? `Total GST (${uniqueGstRates[0]}%)`
      : uniqueGstRates.length > 1
        ? `Total GST (Mixed)`
        : `Total GST`;

  /* ---------------- Handlers ---------------- */
  const handleLocalRemove = (cartItemId, itemName) => {
    removeItem(cartItemId);
    setToastMessage(`"${itemName}" removed from cart`);
    setShowToast(true);
    if (toastTimer) clearTimeout(toastTimer);
    const timer = setTimeout(() => {
      setShowToast(false);
    }, 3000);
    setToastTimer(timer);
  };

  // Coupon Handlers
  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setIsApplyingCoupon(true);
    setCouponMessage({ type: "", text: "" });

    try {
      const response = await couponService.validateCoupon(
        couponInput.trim(),
        totals.grandTotal,
      );
      if (response.valid) {
        setAppliedCoupon({
          code: response.code,
          discountAmount: response.discountAmount,
        });
        setCouponMessage({
          type: "success",
          text: `Coupon applied! You saved ₹${response.discountAmount}`,
        });
        setCouponInput("");
      }
    } catch (error) {
      setCouponMessage({
        type: "error",
        text: error.message || "Invalid coupon code",
      });
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponMessage({ type: "", text: "" });
  };

  /* ---------------- Load Data ---------------- */
  useEffect(() => {
    const loadData = async () => {
      try {
        const [addressData, profileData] = await Promise.all([
          profileService.fetchAddresses(),
          profileService.fetchProfileDetails(),
        ]);

        setAddresses(addressData || []);
        const defaultAddr =
          addressData.find((a) => a.isDefault) || addressData[0];
        setSelectedAddress(defaultAddr || null);
        setUserPhone(profileData.phone || null);
      } catch (err) {
        console.error("Failed to load cart data", err);
      } finally {
        setLoadingAddress(false);
      }
    };
    loadData();
  }, []);

  /* ---------------- Auto-Recalculate Coupon on Cart Change ---------------- */
  useEffect(() => {
    // If no coupon is currently applied, do nothing
    if (!appliedCoupon?.code) return;

    const revalidateCouponSilently = async () => {
      try {
        // Re-run validation with the updated grand total
        const response = await couponService.validateCoupon(
          appliedCoupon.code,
          totals.grandTotal,
        );

        if (response.valid) {
          // Update the discount amount to reflect the new cart total
          setAppliedCoupon({
            code: response.code,
            discountAmount: response.discountAmount,
          });
          setCouponMessage({
            type: "success",
            text: `Coupon applied! You saved ₹${response.discountAmount}`,
          });
        } else {
          // Total might have dropped below a required minimum for this coupon
          setAppliedCoupon(null);
          setCouponMessage({
            type: "error",
            text: "Cart total changed. Coupon is no longer valid.",
          });
        }
      } catch (error) {
        // If the backend throws an error for the new total
        setAppliedCoupon(null);
        setCouponMessage({
          type: "error",
          text: error.message || "Cart total changed. Coupon removed.",
        });
      }
    };

    revalidateCouponSilently();

    // We only want this to run when the total cart value changes,
    // or when a new coupon code is successfully applied.
  }, [totals.grandTotal, appliedCoupon?.code]);

  /* ---------------- Checkout Handler ---------------- */
  /* ---------------- Checkout Handler ---------------- */
  const handleCheckout = async () => {
    if (!selectedAddress) {
      alert("Please select a delivery address");
      return;
    }

    if (!userPhone) {
      alert(
        "Phone number is required to place an order.\n\nYou will be redirected to your profile to add it.",
      );
      onNavigate("account");
      return;
    }

    // Prepare the exact payload we want to send
    const checkoutPayload = {
      cartItems: cartItems,
      shippingFee: shipping,
      couponCode: appliedCoupon ? appliedCoupon.code : null,
      paymentMethod: paymentMethod,
      shippingAddress: {
        name: selectedAddress.fullName,
        line1: selectedAddress.addressLine1,
        city: selectedAddress.city,
        state: selectedAddress.state,
        postal_code: selectedAddress.pincode,
      },
    };

    console.log("Submitting Checkout Payload:", checkoutPayload);

    if (paymentMethod === "COD") {
      try {
        setLoaderMessage("Placing your order…");
        setIsPlacingOrder(true);

        const response = await cartService.placeCODOrder(checkoutPayload);

        onNavigate("payment-success", {
          orderNumber: response.orderNumber,
          orderId: response.orderId,
        });
      } catch (err) {
        console.error("COD order failed", err);
        alert("Failed to place order. Please try again.");
        setIsPlacingOrder(false);
      }
      return;
    }

    // Razorpay Flow
    try {
      setLoaderMessage("Preparing secure payment…");
      setIsPlacingOrder(true);

      const orderData = await cartService.createRazorpayOrder(checkoutPayload);

      setIsPlacingOrder(false);

      if (!orderData || !orderData.amount || !orderData.razorpayOrderId) {
        throw new Error(
          "Invalid response from payment gateway. Amount or Order ID missing.",
        );
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: "INR",
        name: "Zynventics Technologies",
        description: "Order Payment",
        order_id: orderData.razorpayOrderId,

        handler: async (response) => {
          try {
            setLoaderMessage("Confirming payment…");
            setIsPlacingOrder(true);

            await cartService.confirmPayment({
              orderId: orderData.orderId,
            });

            onNavigate("payment-success", {
              orderNumber: orderData.orderNumber,
              orderId: orderData.orderId,
            });
          } catch (err) {
            console.error("Payment confirmation failed", err);
            alert("Payment completed but confirmation failed.");
            setIsPlacingOrder(false);
          }
        },

        // ✅ UPDATED: Handle user closing the modal
        modal: {
          ondismiss: async () => {
            console.log("User closed the Razorpay modal.");
            setIsPlacingOrder(false);

            try {
              // Call an API to mark the order as failed
              await cartService.markPaymentFailed(orderData.orderId);
            } catch (err) {
              console.error("Failed to mark order as payment failed:", err);
            }

            alert("Payment cancelled. You can retry from your Orders page.");
            onNavigate("account"); // Navigate to the orders tab (or wherever you want)
          },
        },
      };

      const rzp = new window.Razorpay(options);

      // ✅ NEW: Handle actual payment failure (card declined, etc.)
      rzp.on("payment.failed", async function (response) {
        setIsPlacingOrder(false);
        try {
          await cartService.markPaymentFailed(orderData.orderId);
        } catch (err) {
          console.error("Failed to mark order as payment failed:", err);
        }
        alert("Payment failed: " + response.error.description);
        onNavigate("account");
      });

      rzp.open();
    } catch (err) {
      console.error("Checkout failed:", err);
      alert(`Unable to initiate payment: ${err.message || "Unknown error"}`);
      setIsPlacingOrder(false);
    }
  };

  /* ---------------- Empty Cart ---------------- */
  if (cartItems.length === 0) {
    return (
      <div className="cart-empty">
        <h2>Your cart is empty</h2>
        <p>Looks like you haven’t added anything yet.</p>
        <button onClick={() => onNavigate("shop")} className="checkout-btn">
          Start Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="cart-page redesigned">
      <div className="checkout-steps">
        <span className="active">Cart</span>
        <span>Checkout</span>
        <span>Success</span>
      </div>

      <div className="cart-layout">
        {/* ================= LEFT ================= */}
        <div className="cart-left">
          <h2>Your Cart ({cartItems.length} items)</h2>

          <div className="cart-table-header">
            <span>Product</span>
            <span>Price</span>
            <span>SKU</span>
            <span>Quantity</span>
            <span>Subtotal</span>
          </div>

          <div className="cart-table-body">
            {cartItems.map((item) => {
              const mrp = Number(
                item.price || item.Inventory?.current_price || 0,
              );
              const hsnCode =
                item.hsn_code || item.Inventory?.hsn_code || "N/A";

              return (
                <div
                  className="cart-table-row"
                  key={item.cartItemId || item.product_id}
                >
                  <div className="product-cell">
                    <img
                      src={
                        item.image ||
                        item.Inventory?.image_url ||
                        "/placeholder.png"
                      }
                      alt={item.name || item.Inventory?.name}
                    />
                    <div className="product-details-col">
                      <div className="product-name">
                        {item.name || item.Inventory?.name}
                      </div>

                      <div className="tax-breakdown-inline">
                        <span className="hsn-badge">HSN: {hsnCode}</span>
                      </div>
                    </div>
                  </div>

                  <div className="price-cell">₹{mrp}</div>

                  <div className="sku-cell">
                    <span className="sku-label">SKU:</span>{" "}
                    {item.sku || item.Inventory?.sku || "—"}
                  </div>

                  <div className="qty-cell">
                    <button
                      onClick={() =>
                        handleQuantityChange(item.cartItemId, item.quantity - 1)
                      }
                      disabled={item.quantity <= 1}
                    >
                      −
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      onClick={() =>
                        handleQuantityChange(item.cartItemId, item.quantity + 1)
                      }
                    >
                      +
                    </button>
                  </div>

                  <div className="subtotal-cell">
                    <span>{formatPrice(mrp * item.quantity)}</span>
                    <button
                      className="delete-item-btn"
                      onClick={() =>
                        handleLocalRemove(
                          item.cartItemId,
                          item.name || item.Inventory?.name,
                        )
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="cart-actions">
            <button
              className="continue-shopping"
              onClick={() => onNavigate("shop")}
            >
              ← Continue Shopping
            </button>

            <button
              className="clear-cart-btn"
              onClick={async () => {
                const confirmClear = window.confirm(
                  "Are you sure you want to remove all items from your cart?",
                );

                if (!confirmClear) return;

                try {
                  await cartService.clearCart();
                  setCartItems([]);
                } catch (err) {
                  alert("Failed to clear cart. Please try again.");
                }
              }}
            >
              <span className="trash-icon">🗑</span>
              <span>Clear Cart</span>
            </button>
          </div>
        </div>

        {/* ================= RIGHT ================= */}
        <div className="cart-right">
          <div className="cart-summary">
            <h3>Order Summary</h3>

            {/* COUPON SECTION */}
            <div className="cart-coupon-section">
              {!appliedCoupon ? (
                <div className="coupon-input-group">
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponInput}
                    onChange={(e) =>
                      setCouponInput(e.target.value.toUpperCase())
                    }
                    disabled={isApplyingCoupon}
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={isApplyingCoupon || !couponInput.trim()}
                  >
                    {isApplyingCoupon ? "..." : "Apply"}
                  </button>
                </div>
              ) : (
                <div className="applied-coupon-badge">
                  <div className="applied-coupon-info">
                    <span className="check-icon">✓</span>
                    <span className="coupon-code-text">
                      '{appliedCoupon.code}' applied
                    </span>
                  </div>
                  <button
                    onClick={handleRemoveCoupon}
                    className="remove-coupon-btn"
                  >
                    Remove
                  </button>
                </div>
              )}
              {couponMessage.text && (
                <p className={`coupon-message ${couponMessage.type}`}>
                  {couponMessage.text}
                </p>
              )}
            </div>

            {/* SUMMARY ROWS */}
            <div className="summary-row">
              <span>Subtotal (Excl. GST)</span>
              <span>{formatPrice(totals.baseTotal)}</span>
            </div>

            <div className="summary-row">
              <span>{gstLabel}</span>
              <span>{formatPrice(totals.taxTotal)}</span>
            </div>

            <div className="summary-row">
              <span>Shipping</span>
              <span>{formatPrice(shipping)}</span>
            </div>

            {/* SHOW DISCOUNT ROW IF COUPON APPLIED */}
            {appliedCoupon && (
              <div className="summary-row discount-row">
                <span>Discount ({appliedCoupon.code})</span>
                <span>- {formatPrice(appliedCoupon.discountAmount)}</span>
              </div>
            )}

            <div className="summary-row total">
              <span>Grand Total</span>
              <span>{formatPrice(finalTotal)}</span>
            </div>
            <p className="tax-inclusive-note">Inclusive of all taxes</p>

            {/* ---------- Address ---------- */}
            <div className="cart-address-section">
              <div className="cart-address-header">
                <h4>Deliver To</h4>
                {!loadingAddress && addresses.length > 0 && (
                  <button
                    className="change-address-btn"
                    onClick={() => onNavigate("account")}
                  >
                    Change
                  </button>
                )}
              </div>

              {loadingAddress ? (
                <p>Loading address...</p>
              ) : addresses.length > 0 ? (
                <div className="cart-address-list">
                  {addresses.map((addr) => (
                    <div
                      key={addr.id}
                      className={`cart-address-card ${
                        selectedAddress?.id === addr.id ? "active" : ""
                      }`}
                      onClick={() => setSelectedAddress(addr)}
                    >
                      <label className="address-radio">
                        <input
                          type="radio"
                          checked={selectedAddress?.id === addr.id}
                          readOnly
                        />
                        <div>
                          <div className="cart-address-name">
                            {addr.fullName}
                          </div>
                          <div className="cart-address-line">
                            {addr.addressLine1}
                          </div>

                          <div className="cart-address-line">
                            {addr.city}, {addr.state} – {addr.pincode}
                          </div>
                          <div className="cart-address-line">
                            {userPhone ? (
                              <>📞 {userPhone}</>
                            ) : (
                              <span className="missing-phone">
                                ⚠ Phone number missing
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="cart-no-address-container">
                  <div className="cart-no-address-icon">📍</div>
                  <p className="cart-no-address-text">
                    No delivery address found
                  </p>
                  <button
                    className="add-address-action-btn"
                    onClick={() => onNavigate("account")}
                  >
                    + Add Address
                  </button>
                </div>
              )}
            </div>

            {/* ---------- Payment ---------- */}
            <div className="payment-method-box">
              <h4>Payment Method</h4>

              <label>
                <input
                  type="radio"
                  checked={paymentMethod === "ONLINE"}
                  onChange={() => setPaymentMethod("ONLINE")}
                />
                Online Payment (UPI / Card)
              </label>

              {/* MODIFIED: Disabled COD Option */}
              <label style={{ cursor: "pointer" }}>
                <input
                  disabled={true}
                  type="radio"
                  checked={paymentMethod === "COD"}
                  onChange={() => setPaymentMethod("COD")}
                />
                Cash on Delivery
              </label>

              <p
                className="cod-note"
                style={{
                  color: "#d9534f",
                  margin: "-5px 0 10px 25px",
                  fontSize: "0.85em",
                }}
              >
                Currently unavailable
              </p>
            </div>

            {/* ---------- Trust ---------- */}
            <div className="trust-box">
              <p>🚚 Ships in 24–48 hours</p>
              <p>🔄 7-day replacement guarantee</p>
              <p>🔒 Secure checkout</p>
            </div>

            <div className="payment-trust-box">
              <p className="trust-title">We accept</p>

              <div className="payment-icons">
                <img src="/payments/upi.svg" alt="UPI" />
                <img src="/payments/visa.svg" alt="Visa" />
                <img src="/payments/mastercard.svg" alt="Mastercard" />
                <img src="/payments/rupay.svg" alt="RuPay" />
              </div>

              <p className="trust-subtext">
                Secure payments powered by Razorpay • 100% safe & encrypted
              </p>
            </div>

            <button
              className="checkout-btn"
              onClick={handleCheckout}
              disabled={isPlacingOrder}
            >
              {isPlacingOrder ? "Please wait…" : "Proceed to Checkout"}
            </button>
          </div>
        </div>
      </div>

      {isPlacingOrder && (
        <div className="order-loader-overlay">
          <div className="order-loader-box">
            <div className="spinner"></div>
            <p>{loaderMessage}</p>
            <span>Please do not refresh the page</span>
          </div>
        </div>
      )}

      {/* FIXED TOAST UI */}
      <div className={`cart-toast-notification ${showToast ? "show" : ""}`}>
        <div className="toast-icon">✓</div>
        <p>{toastMessage}</p>
      </div>
    </div>
  );
};

export default CartPage;
